<template>
  <label
    v-bind="$attrs"
    :title="label"
    :class="[
      $style['form-input-wrapper'],
      { [$style['form-input--search']]: type === 'search' }
    ]">

    <div
      v-if="label && size"
      :for="id"
      :class="$style['form-input-label']">
      {{ label }}
    </div>
    <textarea
      v-if="size"
      :id="id"
      ref="input"
      :style="`resize:${ draggable ? 'vertical' : 'none'}; overflow: auto;`"
      :rows="size"
      :placeholder="placeholder"
      :pattern="pattern"
      :disabled="$attrs.disabled"
      :inputmode="inputMode"
      :autocomplete="autocomplete"
      :type="type"
      :class="[
        $style['form-input'],
        $style['form-input--textarea'],
        { [$style['form-input--dirty']]: validate && validation.$dirty },
        { [$style['form-input--valid']]: validate && !validation.$error },
        { [$style['form-input--validate']]: validate },
        { [$style['form-input--no-border']]: noBorder },
        { [$style['form-input--empty']]: isEmpty },
        { [$style['form-input--loading']]: loading },
        { [$style['form-input--hide-label']]: hideLabel }
      ]"
      :value="modelValue"
      @keyup="onKeyUp"
      @keydown="onKeyDown"
      @focus="onFocus"
      @blur="onBlur"
      @input="onInput" />

    <input
      v-else
      :id="id"
      ref="input"
      :disabled="$attrs.disabled"
      :placeholder="placeholder"
      :pattern="pattern"
      :maxlength="maxlength"
      :tabindex="isHidden ? -1 : 0"
      :inputmode="inputMode"
      :autocomplete="autocomplete"
      :type="type"
      :class="[
        $style['form-input'],
        { [$style['form-input--dirty']]: validate && validation && validation.$dirty },
        { [$style['form-input--valid']]: validate && validation && !validation.$invalid },
        { [$style['form-input--validate']]: validate },
        { [$style['form-input--no-border']]: noBorder },
        { [$style['form-input--empty']]: isEmpty },
        { [$style['form-input--loading']]: loading },
        { [$style['form-input--hide-label']]: hideLabel }
      ]"
      :value="modelValue"
      :min="min"
      :max="max"
      @keyup="onKeyUp"
      @keydown="onKeyDown"
      @focus="onFocus"
      @focusout="onFocusOut"
      @blur="onBlur"
      @change="onChange"
      @input="onInput">

    <div
      v-if="label"
      :for="id"
      :class="$style['form-input-label']">
      {{ label }}
    </div>

    <div
      v-if="loading"
      :class="$style['form-input-spinner']">
      <LazySpinner v-if="loading" />
    </div>

    <div
      v-if="type === 'search' && !loading"
      :class="$style['form-input__icon']">
      <Btn
        type="button"
        shape="round"
        :tabindex="isHidden ? -1 : 0"
        color="link"
        aria-label="Search"
        :icon-only="modelValue ? 'System/close-line' : 'System/search-line'"
        @click="$emit('close')" />
    </div>

    <div v-if="$slots.button" :class="$style['form-input__icon']">
      <slot name="button" />
    </div>
    <LazyFormValidation
      v-if="
        (validation && validation.$dirty && validation.$invalid) ||
          customValidation ||
          (dirty && error)
      "
      data-test-id="validation"
      :validation="validation"
      :custom-validation="customValidation" />
  </label>
</template>

<script setup>
import { parseISO, differenceInYears } from 'date-fns'

defineOptions({
  inheritAttrs: false
})

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'focusout', 'keyup', 'keydown'])

const props = defineProps({
  id: {
    type: [String, Number, Boolean],
    default: null,
    required: false
  },
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
    required: false
  },
  type: {
    type: String,
    default: 'text',
    required: false
  },
  autocomplete: {
    type: String,
    default: null,
    required: false
  },
  placeholder: {
    type: [String, Boolean],
    default: '',
    required: false
  },
  label: {
    type: [String],
    default: null,
    required: false
  },
  noBorder: {
    type: Boolean,
    default: false
  },
  hideLabel: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false,
    required: false
  },
  error: {
    type: Boolean,
    default: false
  },
  dirty: {
    type: Boolean,
    default: false
  },
  size: {
    type: Number,
    default: null,
    required: false
  },
  draggable: {
    type: Boolean,
    default: false
  },
  // If you want to set the focus from outside the component
  hasFocus: {
    type: Boolean,
    required: false,
    default: false
  },
  inputMode: {
    type: String,
    default: null
  },
  pattern: {
    type: String,
    default: null
  },
  /**
     *True if inputs needs validation
     */
  validate: Boolean,
  /**
     * Validation options from vuelidate.
     */
  validation: [Boolean, Object],
  /**
     * Validation rules from api
     */
  validationRules: {
    type: [Object, Array],
    required: false,
    default: null
  },

  min: {
    type: [Number, String, Boolean],
    default: null
  },
  max: {
    type: [Number, String, Boolean],
    default: null
  },
  maxlength: {
    type: [Number, String, Boolean],
    default: null
  },
  errors: {
    type: String,
    default: null
  },
  isHidden: {
    type: Boolean,
    default: false
  }
})

const customValidation = ref({})
const input = ref(null)

const isEmpty = computed(() => {
  return !props.modelValue
})

const minMax = () => {
  if (props.min && parseInt(props.modelValue) < parseInt(props.min - 1)) {
    emit('update:modelValue', props.min)
  }
  if (props.max && parseInt(props.modelValue) > parseInt(props.max)) {
    emit('update:modelValue', props.max)
  }
}
const onInput = event => {
  emit('update:modelValue', event.target.value)
}
const onChange = event => {
  emit('update:modelValue', event.target.value)
}
const onKeyUp = () => {
  emit('keyup')
}
const onKeyDown = () => {
  emit('keydown')
}
const onBlur = () => {
  if (props.validation) { props.validation.$touch() }
  emit('blur')
}
const onFocus = () => {
  emit('focus')
}
const onFocusOut = () => {
  if (props.validation) { props.validation.$touch() }
  emit('focusout')
}
const focus = () => {
  input.value.focus()
}
const select = () => {
  input.value.select()
}
const customValidate = () => {
  if (!props.validationRules) { return }
  customValidation.value.$dirty = true
  if (props.validationRules.includes('digits:19')) {
    customValidation.value.digit19 = (props.modelValue && props.modelValue.length !== 19)
  }
  if (props.validationRules.includes('digits:6')) {
    customValidation.value.digit6 = (props.modelValue && props.modelValue.length !== 6)
  }
  if (props.validationRules.includes('before:18 years ago')) {
    customValidation.value.before18 = props.modelValue && differenceInYears(new Date(), parseISO(props.modelValue)) < 18
  }
  if (props.validationRules.includes('after:120 years ago')) {
    customValidation.value.after120 = props.modelValue && differenceInYears(new Date(), parseISO(props.modelValue)) > 120
  }
}

watch(() => props.modelValue, () => {
  customValidate()
  minMax()
})

onMounted(() => {
  customValidate()
})

defineExpose({ focus, select })
</script>

<style lang="scss" module>
// @TODO: convert validation to module
.form-input-wrapper {
  display: flex;
  position: relative;
  flex-direction: column;
  z-index: 1;
  container: wrapper / inline-size;

  &:hover {
    .form-input {
      border-color: $black;
    }
  }
}

.form-input-label {
  align-items: center;

  background-color: $white;
  color: $grey-dark;
  display: inline-flex;
  pointer-events: none;
  position: absolute;
  top: 0;
  z-index: 1;
  font-size: $font-size-s;
  transform: translateY($spacing * 1.5);
  margin-left: $spacing;
  transition: transform $transition-time $transition-easing;
  transform-origin: 0 50%;
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100cqw - $spacing * 2);
}

.form-input-spinner {
  position: absolute;
  right: $spacing * 2;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 800; // @TODO: Find out way this high. Should be relative to its parent element
}

.form-input {
  background-color: $white;
  border: 0;
  border: 1px solid $grey-light;
  border-radius: $border-radius;

  color: $black;
  display: inline-flex;
  font-size: $font-size !important;
  font-weight: $font-weight-normal;
  margin: 0;
  min-height: $spacing * 6;
  width: 100%;
  padding: $spacing * 1.75 $spacing * 2;
  transition: all $transition-time $transition-easing;
  appearance: none;
  z-index: 1;
  position: relative;

  &--textarea {
    height: auto;
    resize: none;
  }

  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    appearance: none;
  }

  &[type='date'] {
    height: 3rem;

    &::-webkit-datetime-edit {
      opacity: 0;
    }

    &::-webkit-calendar-picker-indicator {
      padding: 0;
    }

    &::-webkit-datetime-edit-fields-wrapper {
      padding: 0;
    }
  }

  &:focus {
    border-color: $black;
  }

  &::placeholder {
    color: $grey-dark;
    font-weight: $font-weight-normal;
  }

  &:-webkit-autofill {
    font-size: $font-size !important;
    transition: background-color 5000s ease-in-out 0s; // to Prevent autofill styles from browser
  }

  &[disabled] {
    border-color: $grey-lighter;
    color: $grey;
    pointer-events: none;
  }

  &[readonly],
  &[disabled] {
    &,
    &:hover,
    &:focus {
      border-color: $grey-lighter;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    fill: $black;
    position: absolute;
    right: $spacing;
    bottom: 0;
    top: 0;
    height: auto;
    z-index: 1;
  }

  &--search {
    width: 100%;

    input {
      border-radius: 9999px;
    }
  }

  &--no-border {
    border: 0;

    &:focus,
    &:active {
      box-shadow: none;
    }
  }

  &--validate {
    transition: background-position 150ms ease, border 150ms ease;
    background-repeat: no-repeat;
    background-position: right ($spacing * 2) bottom -150%;
    background-size: $spacing * 2;
    background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 48 48" enable-background="new 0 0 48 48"><polygon fill="%2300b900" points="40.6,12.1 17,35.7 7.4,26.1 4.6,29 17,41.3 43.4,14.9"/></svg>');

    &.form-input--valid {
      background-position: right ($spacing * 2) center;
    }

    &.form-input--dirty:not(.form-input--valid) {
      border-bottom-color: rgba($red, 0.75);
    }
  }

  &:disabled:not(.form-input--empty) {
    & ~ .form-input-label {
      transform: scale(0.9) translateY(-55%);
    }
  }

  &[type='file'],
  &:focus,
  &:not(.form-input--empty) {
    &::-webkit-datetime-edit {
      opacity: 1;
    }

    & ~ .form-input-label {
      transform: scale(0.9) translateY(-55%);
    }
  }

  &--hide-label.form-input:focus,
  &--hide-label.form-input:not(.form-input--empty) {
    & ~ .form-input-label {
      opacity: 0;
      transform: none;
      transition: 0;
    }
  }
}
</style>
